import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';

import { useDetailNavigationScrollOffset } from '#/components/detail/useDetailNavigationScrollOffset';
import { Icon } from '#/components/ui/icons';
import { useHorizontalScrollState } from '#/hooks/useHorizontalScrollState';
import { cn } from '#/utils/cn';

export interface StickySectionEntry {
  id: string;
  label: string;
}

interface StickySectionNavProps {
  sections: StickySectionEntry[];
  onSelect: (id: string) => void;
  /** Id of the section the page currently considers active (scroll spy). */
  activeSection?: string;
  /**
   * Moves focus past the nav into the page content. Rendered as a
   * visually-hidden skip link so keyboard and screen reader users are not
   * forced through every section button on the way to the content. A
   * callback rather than an anchor because the app can render inside a
   * shadow root, where fragment (`#id`) navigation cannot find its target.
   */
  onSkip?: () => void;
  /**
   * Mobile-only condensed summary row rendered above the section list once
   * the page's in-place summary has scrolled out of view.
   */
  condensedSummary?: ReactNode;
  /**
   * Optional GTM annotation for the section jump buttons. When provided, each
   * section `<button>` receives `data-track` / `data-track-context` (label is
   * the section label). Omitted leaves the buttons to generic auto-capture, so
   * callers that do not pass it (e.g. the detail page) are unchanged.
   */
  jumpTrack?: { track: string; context: string };
}

const EDGE_FADE_CLASSES =
  'from-gray-10 pointer-events-none absolute inset-y-0 w-40 to-transparent lg:hidden';

/**
 * Section navigation that pins below the nav. On mobile views, it allows horizontal
 * scrolling and contains a condensed summary once the user scrolls past the
 * initial security summary.
 */
export function StickySectionNav({
  sections,
  onSelect,
  activeSection,
  onSkip,
  condensedSummary,
  jumpTrack,
}: StickySectionNavProps) {
  const { setPageNavElement } = useDetailNavigationScrollOffset();
  const listRef = useRef<HTMLUListElement>(null);
  const { canScrollLeft, canScrollRight } = useHorizontalScrollState(listRef);

  // Keep the active item visible in the mobile strip. Centering math instead
  // of scrollIntoView because the latter may also scroll ancestors (the page)
  // while a jump-link smooth scroll is in flight.
  useEffect(() => {
    const list = listRef.current;

    if (!list || !activeSection || list.scrollWidth <= list.clientWidth) {
      return;
    }

    const item = list.querySelector<HTMLElement>(
      `[data-section-id="${activeSection}"]`,
    );

    if (!item) {
      return;
    }

    const listRect = list.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const targetLeft =
      list.scrollLeft +
      (itemRect.left - listRect.left) -
      (list.clientWidth - itemRect.width) / 2;

    list.scrollTo({ left: targetLeft, behavior: 'smooth' });
  }, [activeSection]);

  const nextSection =
    activeSection !== undefined
      ? sections[sections.findIndex(({ id }) => id === activeSection) + 1]
      : undefined;

  return (
    <nav
      ref={setPageNavElement}
      aria-label="Sections"
      className="col-full lg:col-leftside border-blue-30 mb-gap bg-gray-10 sticky top-(--category-navigation-scroll-offset) z-30 self-start border-b shadow-lg lg:top-(--category-navigation-top) lg:border-0 lg:bg-transparent lg:pt-0 lg:shadow-none"
    >
      {onSkip && (
        <button
          type="button"
          className="mdc-btn mdc-btn--sm mdc-btn--primary sr-only absolute top-8 left-8 z-50 focus:not-sr-only focus:absolute focus:px-8 focus:py-4"
          onClick={onSkip}
        >
          Skip section navigation
        </button>
      )}
      {condensedSummary}
      <div className="flex items-center lg:block">
        <div className="relative min-w-0 flex-1 lg:static">
          <ul
            ref={listRef}
            className="text-fluid-base-sm lg:flow flow-8 px-gap scrollbar-custom flex w-full scroll-mt-(--scroll-offset) flex-row flex-nowrap items-center gap-16 overflow-x-auto py-16 lg:block lg:p-0"
          >
            {sections.map(({ id: sectionId, label }) => {
              const isActive = sectionId === activeSection;

              return (
                <li key={sectionId}>
                  <button
                    type="button"
                    data-section-id={sectionId}
                    data-track={jumpTrack?.track}
                    data-track-label={jumpTrack ? label : undefined}
                    data-track-context={jumpTrack?.context}
                    aria-current={isActive ? 'location' : undefined}
                    className={cn(
                      'focus-visible:outline-button-primary-bg shrink-0 border-b-4 text-left leading-relaxed whitespace-pre focus-visible:outline-2 focus-visible:outline-offset-2 sm:border-b-0 lg:whitespace-normal',
                      isActive
                        ? 'text-fg-header border-fg-header font-semibold'
                        : 'text-link-fg hover:text-link-fg-hover border-transparent',
                    )}
                    onClick={() => onSelect(sectionId)}
                  >
                    {label}
                  </button>
                </li>
              );
            })}
          </ul>
          {canScrollLeft && (
            <div
              aria-hidden="true"
              data-testid="section-nav-fade-left"
              className={`${EDGE_FADE_CLASSES} left-0 bg-linear-to-r`}
            />
          )}
          {canScrollRight && (
            <div
              aria-hidden="true"
              data-testid="section-nav-fade-right"
              className={`${EDGE_FADE_CLASSES} right-0 bg-linear-to-l`}
            />
          )}
        </div>
        {nextSection && (
          <button
            type="button"
            aria-label={`Jump to ${nextSection.label}`}
            className="text-link-fg hover:text-link-fg-hover pr-gap focus-visible:outline-button-primary-bg shrink-0 py-16 pl-8 focus-visible:outline-2 focus-visible:outline-offset-2 lg:hidden"
            onClick={() => onSelect(nextSection.id)}
          >
            <Icon id="chevron-right" size="16" />
          </button>
        )}
      </div>
    </nav>
  );
}
